import { StarIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { IconButton, Spinner } from '@/components/common';
import { useCurrentUser } from '@/contexts';
import { useFavorites } from '@/hooks';
import { useFilterStore } from '@/stores/filterStore';

interface FavoriteButtonProps {
  uid: string;
}

/** Star toggle in the top bar of another member's profile. */
export function FavoriteButton({ uid }: FavoriteButtonProps) {
  const { t } = useTranslation('profile');
  const navigate = useNavigate();
  const { user: authUser } = useCurrentUser();
  const { isFavorite, isPending, toggleFavorite } = useFavorites(authUser);

  const favorited = isFavorite(uid);
  const pending = isPending(uid);

  const handleToggle = async () => {
    const added = !favorited;
    const ok = await toggleFavorite(uid);
    if (!ok) {
      toast.error(t('view.favoriteError'));
      return;
    }
    if (added) {
      // Teach what the star does: favorites live behind the header toggle.
      toast.success(t('view.favoriteAdded.title'), {
        description: t('view.favoriteAdded.description'),
        action: {
          label: t('view.favoriteAdded.action'),
          onClick: () => {
            useFilterStore.getState().setFavoritesOnly(true);
            navigate('/discover');
          },
        },
      });
    } else {
      toast.success(t('view.favoriteRemoved.title'), {
        action: {
          label: t('view.favoriteRemoved.undo'),
          onClick: () => void toggleFavorite(uid),
        },
      });
    }
  };

  return (
    <IconButton
      type="button"
      variant={favorited ? 'solid' : 'outline'}
      color={favorited ? 'red' : 'gray'}
      highContrast={!favorited}
      radius="full"
      size="2"
      disabled={pending}
      data-testid="favorite-toggle"
      aria-label={t(favorited ? 'view.actions.unfavorite' : 'view.actions.favorite')}
      aria-pressed={favorited}
      aria-busy={pending}
      onClick={() => void handleToggle()}
    >
      {pending ? (
        <Spinner size="1" />
      ) : (
        <StarIcon size={20} fill={favorited ? 'currentColor' : 'none'} aria-hidden />
      )}
    </IconButton>
  );
}
